/* global SideDrawer */

/**
 * Box Builder — pick products into a box; Shopify cart is the source of truth.
 */
(function () {
  if (window.BoxBuilderApp) return;

  const formatMoney = (cents) => {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney && theme?.settings?.moneyWithCurrencyFormat) {
      return Shopify.formatMoney(cents, theme.settings.moneyWithCurrencyFormat);
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'de', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  };

  class BoxBuilder extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.minItems = 1;
      this.maxItems = 8;
      this.cartTotal = 0;
    }

    connectedCallback() {
      this.minItems = Number(this.dataset.minItems) || 1;
      this.maxItems = Number(this.dataset.maxItems) || 8;
      this.storageKey = this.dataset.storageKey || 'box-builder';
      this.giftThreshold = Number(this.dataset.giftThreshold) || 2;
      this.giftImage = this.dataset.giftImage || '';
      this.giftTitle = this.dataset.giftTitle || 'Deep Sleep Mask';
      this.giftBadge = this.dataset.giftBadge || 'Gratis';
      this.giftPacked = false;
      this.giftPacking = false;
      this.giftAnim = null;
      this.giftFlyEl = null;
      this.restoring = true;
      this.cartBusy = false;
      this.syncingCart = false;

      this.slotsEl = this.querySelector('[data-box-slots]');
      this.giftBayEl = this.querySelector('[data-box-gift-bay]');
      this.interiorEl = this.querySelector('[data-box-interior]');
      this.emptyEl = this.querySelector('[data-box-empty]');
      this.countEls = this.querySelectorAll('[data-box-item-count], [data-mobile-count]');
      this.errorEl = this.querySelector('[data-box-error]');
      this.checkoutBtns = this.querySelectorAll('[data-box-checkout]');
      this.panel = this.querySelector('[data-box-panel]');
      this.mobileToggle = this.querySelector('[data-mobile-toggle]');
      this.stage = this.querySelector('[data-box-stage]');
      this.tiersEl = this.querySelector('[data-box-tiers]');
      this.tiersStatusEl = this.querySelector('[data-box-tiers-status]');
      this.ladderEl = this.querySelector('.box-builder__ladder');

      this.items = [];
      this.bindCartSync();
      this.hydrateFromCart();

      this.checkoutBtns.forEach((btn) => {
        btn.addEventListener('click', () => this.checkout());
      });

      if (this.mobileToggle) {
        this.mobileToggle.addEventListener('click', () => this.toggleMobilePanel());
      }

      const mobileClose = this.querySelector('[data-mobile-close]');
      if (mobileClose) {
        mobileClose.addEventListener('click', () => this.setMobileExpanded(false));
      }

      this.slotsEl?.addEventListener('click', (evt) => {
        const removeBtn = evt.target.closest('[data-box-remove]');
        if (!removeBtn) return;
        this.removeItem(Number(removeBtn.dataset.boxRemove));
      });
    }

    loadState() {
      return [];
    }

    saveState() {
      /* Cart is the source of truth. */
    }

    itemCount() {
      return this.items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);
    }

    gwpConfig() {
      const el = document.getElementById('eantas-upcart-gwp-config');
      if (!el) return {};
      try {
        return JSON.parse(el.textContent);
      } catch (error) {
        return {};
      }
    }

    isPyjamaCartLine(line) {
      const config = this.gwpConfig();
      const giftId = String(config.giftProductId || '');
      const ids = new Set((config.pyjamaIds || []).map(String));
      const productId = String(line.product_id || line.productId || '');
      const handle = String(line.handle || '').toLowerCase();
      if ((giftId && productId === giftId) || /sleep-mask|schlafmask/.test(handle)) return false;
      return ids.has(productId) || /pyjama/.test(handle);
    }

    foldedImageMap() {
      if (this._foldedMap) return this._foldedMap;
      const el = this.querySelector('[data-folded-images]');
      try {
        this._foldedMap = JSON.parse(el?.textContent || '{}');
      } catch (error) {
        this._foldedMap = {};
      }
      return this._foldedMap;
    }

    foldedImageFor(variantId, fallback) {
      return this.foldedImageMap()[String(variantId)] || fallback || '';
    }

    cartLineToItem(line) {
      const variantId = line.variant_id || line.id;
      const fallback = typeof line.image === 'string'
        ? line.image
        : (line.featured_image && (line.featured_image.url || line.featured_image.src)) || '';
      return {
        key: line.key,
        variantId,
        productId: line.product_id,
        quantity: Number(line.quantity) || 1,
        title: line.product_title || line.title,
        variantTitle: line.variant_title,
        image: this.foldedImageFor(variantId, fallback),
        price: Number(line.price) || 0,
        handle: line.handle
      };
    }

    applyCart(cart) {
      const lines = (cart && cart.items) || [];
      this.items = lines.filter((line) => this.isPyjamaCartLine(line)).map((line) => this.cartLineToItem(line));
      this.cartTotal = Number(cart && cart.total_price) || 0;
      this.renderSlots();
      this.updateSummary();
    }

    async hydrateFromCart({ animateGift = false } = {}) {
      this.restoring = !animateGift;
      try {
        const response = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!response.ok) return;
        this.applyCart(await response.json());
      } catch (error) {
        this.items = [];
        this.cartTotal = 0;
        this.renderSlots();
        this.updateSummary();
      } finally {
        this.restoring = false;
      }
    }

    notifyCartChanged() {
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      if (typeof window.upcartRefreshCart === 'function') {
        window.upcartRefreshCart();
      }
    }

    bindCartSync() {
      this.onExternalCartChange = () => {
        if (this.syncingCart) return;
        this.hydrateFromCart();
      };
      ['cart:change', 'cart:updated', 'on:cart:change', 'on:cart:after-merge'].forEach((name) => {
        document.addEventListener(name, this.onExternalCartChange);
      });

      const attachUpcart = () => {
        ['upcartSubscribeCartUpdated', 'upcartSubscribeItemRemoved', 'upcartSubscribeAddedToCart'].forEach((name) => {
          if (typeof window[name] === 'function') {
            window[name](() => this.onExternalCartChange());
          }
        });
        if (!this.upcartSilenceBound && typeof window.upcartSubscribeCartOpened === 'function') {
          window.upcartSubscribeCartOpened(() => {
            if (!this.upcartSilent) return;
            if (typeof window.upcartCloseCart === 'function') window.upcartCloseCart();
          });
          this.upcartSilenceBound = true;
        }
      };
      if (typeof window.upcartSubscribeCartUpdated === 'function') {
        attachUpcart();
        return;
      }
      let tries = 0;
      const timer = window.setInterval(() => {
        tries += 1;
        if (typeof window.upcartSubscribeCartUpdated === 'function') {
          window.clearInterval(timer);
          attachUpcart();
        } else if (tries >= 50) {
          window.clearInterval(timer);
        }
      }, 200);
    }

    unbindCartSync() {
      if (!this.onExternalCartChange) return;
      ['cart:change', 'cart:updated', 'on:cart:change', 'on:cart:after-merge'].forEach((name) => {
        document.removeEventListener(name, this.onExternalCartChange);
      });
    }

    canAddMore() {
      return this.itemCount() < this.maxItems;
    }

    beginSilentCartWrite() {
      const settings = window.upcartSettings && window.upcartSettings.upcartSettings;
      if (settings) {
        this._upcartOpenOnAdd = settings.openOnAddToCart;
        settings.openOnAddToCart = false;
      }
      this.upcartSilent = true;
      document.body.classList.add('box-builder-cart-silent');
    }

    endSilentCartWrite() {
      window.setTimeout(() => {
        const settings = window.upcartSettings && window.upcartSettings.upcartSettings;
        if (settings && this._upcartOpenOnAdd !== undefined) {
          settings.openOnAddToCart = this._upcartOpenOnAdd;
        }
        this.upcartSilent = false;
        document.body.classList.remove('box-builder-cart-silent');
      }, 800);
    }

    async addItem(item) {
      if (!item?.variantId) return false;

      if (!this.canAddMore()) {
        this.setError(`Maximal ${this.maxItems} Produkte in der Box.`);
        return false;
      }

      if (this.cartBusy) return false;

      this.setError('');
      this.cartBusy = true;
      this.syncingCart = true;
      this.beginSilentCartWrite();

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: item.variantId, quantity: 1 }]
          })
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Konnte nicht in den Warenkorb gelegt werden.');
        }
        await this.hydrateFromCart({ animateGift: true });
        this.notifyCartChanged();

        if (window.matchMedia('(max-width: 999.98px)').matches) {
          this.setMobileExpanded(true);
        }
        return true;
      } catch (error) {
        this.setError(error.message || 'Konnte nicht in den Warenkorb gelegt werden.');
        return false;
      } finally {
        this.endSilentCartWrite();
        this.cartBusy = false;
        this.syncingCart = false;
      }
    }

    async removeItem(variantId) {
      const line = this.items.find((entry) => String(entry.variantId) === String(variantId));
      if (!line || this.cartBusy) return;

      const nextQty = Math.max(0, (Number(line.quantity) || 1) - 1);
      this.cartBusy = true;
      this.syncingCart = true;
      try {
        const payload = line.key
          ? { id: line.key, quantity: nextQty }
          : { id: line.variantId, quantity: nextQty };
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Artikel konnte nicht entfernt werden.');
        }
        await this.hydrateFromCart();
        this.notifyCartChanged();
        this.setError('');
      } catch (error) {
        this.setError(error.message || 'Artikel konnte nicht entfernt werden.');
      } finally {
        this.cartBusy = false;
        this.syncingCart = false;
      }
    }

    toggleMobilePanel() {
      if (!this.panel) return;
      this.setMobileExpanded(!this.panel.classList.contains('is-expanded'));
    }

    setMobileExpanded(expanded) {
      if (!this.panel) return;
      this.panel.classList.toggle('is-expanded', expanded);
      if (this.mobileToggle) this.mobileToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      document.body.classList.toggle('box-builder-open', expanded);
      this.setBoxOpen(expanded);
    }

    disconnectedCallback() {
      document.body.classList.remove('box-builder-open');
      this.unbindCartSync();
    }

    // Der Deckel klappt erst auf, wenn das Panel oben steht, sonst laeuft die
    // Animation hinter dem Bildschirmrand ab. Zuklappen laeuft mit dem Panel mit.
    setBoxOpen(open) {
      if (!this.stage) return;
      clearTimeout(this.lidTimer);
      if (!open) {
        this.stage.classList.add('is-closed');
        return;
      }
      this.lidTimer = setTimeout(() => this.stage.classList.remove('is-closed'), 320);
    }

    setError(message) {
      if (!this.errorEl) return;
      if (!message) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
        return;
      }
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    }

    createProductSlot(item) {
      const slot = document.createElement('div');
      slot.className = 'box-builder__slot';
      slot.dataset.variantId = String(item.variantId);

      const img = document.createElement('img');
      img.src = item.image || '';
      img.alt = item.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 84;
      img.height = 126;

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'box-builder__slot-remove';
      removeBtn.dataset.boxRemove = String(item.variantId);
      removeBtn.setAttribute('aria-label', 'Entfernen');
      removeBtn.textContent = '×';

      slot.append(img, removeBtn);
      return slot;
    }

    createGiftSlot() {
      const slot = document.createElement('div');
      slot.className = 'box-builder__slot box-builder__slot--gift';
      slot.dataset.giftSlot = '';
      slot.setAttribute('aria-label', `${this.giftBadge}: ${this.giftTitle}`);

      if (this.giftImage) {
        const img = document.createElement('img');
        img.src = this.giftImage;
        img.alt = this.giftTitle;
        img.width = 84;
        img.height = 126;
        img.decoding = 'async';
        slot.appendChild(img);
      }

      const badge = document.createElement('span');
      badge.className = 'box-builder__slot-badge';
      badge.textContent = this.giftBadge;
      slot.appendChild(badge);
      return slot;
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    clearGiftFly() {
      if (this.giftAnim) {
        this.giftAnim.cancel();
        this.giftAnim = null;
      }
      if (this.giftFlyEl) {
        this.giftFlyEl.remove();
        this.giftFlyEl = null;
      }
    }

    animateGiftPack(slot) {
      const reveal = () => {
        this.giftPacking = false;
        if (!slot.isConnected || this.itemCount() < this.giftThreshold) return;
        slot.classList.add('is-packed');
        this.giftPacked = true;
      };

      const thumb = this.querySelector('[data-gift-thumb]');
      const delay = window.matchMedia('(max-width: 999.98px)').matches ? 280 : 40;

      if (this.prefersReducedMotion() || !thumb) {
        reveal();
        return;
      }

      window.setTimeout(() => {
        if (!slot.isConnected || this.itemCount() < this.giftThreshold) {
          this.giftPacking = false;
          return;
        }

        const from = thumb.getBoundingClientRect();
        const to = slot.getBoundingClientRect();
        if (from.width < 4 || to.width < 4) {
          reveal();
          return;
        }

        this.clearGiftFly();

        const fly = thumb.cloneNode(true);
        fly.classList.add('box-builder__gift-fly');
        fly.removeAttribute('data-gift-thumb');
        const fromCx = from.left + from.width / 2;
        const fromCy = from.top + from.height / 2;
        Object.assign(fly.style, {
          left: `${fromCx}px`,
          top: `${fromCy}px`,
          width: `${from.width}px`,
          height: `${from.height}px`
        });
        document.body.appendChild(fly);
        this.giftFlyEl = fly;

        const dx = to.left + to.width / 2 - fromCx;
        const dy = to.top + to.height / 2 - fromCy;
        const scale = Math.max(to.width / from.width, 0.6);

        const animation = fly.animate(
          [
            { transform: 'translate(-50%, -50%) translate(0px, 0px) scale(1) rotate(0deg)', opacity: 1 },
            {
              transform: `translate(-50%, -50%) translate(${dx * 0.52}px, ${dy * 0.28 - 22}px) scale(1.2) rotate(-14deg)`,
              opacity: 1,
              offset: 0.46
            },
            {
              transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(${scale}) rotate(0deg)`,
              opacity: 1
            }
          ],
          { duration: 620, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }
        );
        this.giftAnim = animation;

        animation.finished
          .then(() => {
            reveal();
            this.clearGiftFly();
          })
          .catch(() => {
            reveal();
            this.clearGiftFly();
          });
      }, delay);
    }

    syncGiftSlot() {
      const host = this.giftBayEl || this.slotsEl;
      if (!host) return;

      let slot = host.querySelector('[data-gift-slot]');

      if (this.itemCount() === 0) {
        this.clearGiftFly();
        this.giftPacking = false;
        if (slot) slot.remove();
        this.giftPacked = false;
        return;
      }

      if (!slot) {
        slot = this.createGiftSlot();
        host.appendChild(slot);
      }

      const shouldPack = this.itemCount() >= this.giftThreshold;

      if (!shouldPack) {
        this.clearGiftFly();
        this.giftPacking = false;
        slot.classList.remove('is-packed');
        this.giftPacked = false;
        return;
      }

      if (this.giftPacked || this.restoring) {
        slot.classList.add('is-packed');
        this.giftPacked = true;
        this.giftPacking = false;
        return;
      }

      if (this.giftPacking) return;
      this.giftPacking = true;
      this.animateGiftPack(slot);
    }

    fillTierCopy(template, vars) {
      return Object.keys(vars).reduce(
        (text, key) => text.replaceAll(`__${key}__`, String(vars[key])),
        template || ''
      );
    }

    updateTiers() {
      const count = this.itemCount();
      const nodes = [...this.querySelectorAll('[data-box-tier]')];
      if (!nodes.length) return;

      const next = nodes.find((el) => count < Number(el.dataset.boxTier)) || null;
      const unlockedNodes = nodes.filter((el) => count >= Number(el.dataset.boxTier));
      const highestDiscount = [...unlockedNodes].reverse().find((el) => el.dataset.tierType !== 'gift') || null;
      const nextIndex = next ? nodes.indexOf(next) : nodes.length - 1;

      nodes.forEach((el, index) => {
        const need = Number(el.dataset.boxTier);
        const unlocked = count >= need;
        const isGift = el.dataset.tierType === 'gift';
        const passed = unlocked && !isGift && highestDiscount && el !== highestDiscount;
        const current = next ? el === next : unlocked && index === nodes.length - 1;
        const depth = current ? 0 : Math.max(0, index - nextIndex);

        el.classList.toggle('is-unlocked', unlocked);
        el.classList.toggle('is-passed', Boolean(passed));
        el.classList.toggle('is-current', current);
        el.dataset.depth = String(depth);
      });

      const unlockedCount = unlockedNodes.length;
      const progress = nodes.length > 1 ? (unlockedCount / (nodes.length - 1)) * 100 : unlockedCount ? 100 : 0;
      this.ladderEl?.style.setProperty('--tier-progress', `${Math.min(100, progress)}%`);

      if (!this.tiersStatusEl || !this.tiersEl) return;

      const giftNeed = Number(nodes[0]?.dataset.boxTier) || 2;
      if (count < giftNeed) {
        this.tiersStatusEl.textContent = this.fillTierCopy(this.tiersEl.dataset.msgGift, {
          n: giftNeed - count
        });
        return;
      }

      if (next) {
        const currentReward = (highestDiscount || unlockedNodes[unlockedNodes.length - 1])
          ?.querySelector('.box-builder__tier-reward')
          ?.textContent.trim() || '';
        const nextReward = next.querySelector('.box-builder__tier-reward')?.textContent.trim() || '';
        this.tiersStatusEl.textContent = this.fillTierCopy(this.tiersEl.dataset.msgNext, {
          current: currentReward,
          n: Number(next.dataset.boxTier) - count,
          next: nextReward
        });
        return;
      }

      this.tiersStatusEl.textContent = this.tiersEl.dataset.msgMax || '';
    }

    renderSlots() {
      if (!this.slotsEl) return;

      this.slotsEl.replaceChildren();
      this.items.forEach((item) => {
        const qty = Number(item.quantity) || 1;
        for (let i = 0; i < qty; i += 1) {
          this.slotsEl.appendChild(this.createProductSlot(item));
        }
      });

      const count = this.itemCount();
      this.interiorEl?.classList.toggle('has-items', count > 0);
      if (this.interiorEl) this.interiorEl.dataset.packCount = String(count);

      if (this.emptyEl) {
        this.emptyEl.hidden = count > 0;
      }

      this.syncGiftSlot();
    }

    updateSummary() {
      const count = this.itemCount();
      const subtotal = this.items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

      this.countEls.forEach((el) => {
        el.textContent = String(count);
      });

      const canCheckout = count >= 1 && count <= this.maxItems;
      const rawLabel = this.dataset.checkoutLabel || 'Jetzt eintüten';
      const checkoutLabel = /warenkorb|bestellen/i.test(rawLabel) ? 'Jetzt eintüten' : rawLabel;
      const payable = this.cartTotal || subtotal;
      this.checkoutBtns.forEach((btn) => {
        btn.disabled = !canCheckout;
        const labelEl = btn.querySelector('[data-box-checkout-label]');
        const priceEl = btn.querySelector('[data-box-checkout-price]');
        if (labelEl) labelEl.textContent = checkoutLabel;
        if (priceEl) {
          priceEl.hidden = !canCheckout;
          priceEl.textContent = canCheckout ? formatMoney(payable) : '';
        }
      });

      this.updateTiers();
    }

    async checkout() {
      if (this.itemCount() < 1) {
        this.setError('Die Box ist noch leer.');
        return;
      }

      this.setError('');
      window.location.href = '/checkout';
    }

    escapeAttr(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;');
    }
  }

  const BoxBuilderPicker = {
    drawer: null,
    addBtn: null,
    activeBuilder: null,
    activeBlock: null,
    abortController: null,
    currentProductUrl: null,
    addLabel: 'Zur Box hinzufügen',

    init() {
      this.drawer = document.querySelector('.js-box-builder-drawer');
      this.addBtn = this.drawer?.querySelector('[data-box-builder-drawer-add]');

      document.addEventListener('click', this.handlePickClick.bind(this), true);

      if (this.addBtn) {
        this.addBtn.addEventListener('click', () => this.handleAddToBox('drawer'));
      }

      if (this.drawer) {
        this.drawer.addEventListener('on:variant:change', () => this.syncDrawerAddState());
      }

      document.addEventListener('on:variant:change', (evt) => {
        if (this.drawer?.contains(evt.target)) this.syncDrawerAddState();
        if (this.activeBlock?.contains(evt.target)) this.syncInlineAddState();
      });

      document.addEventListener('on:quickbuy:after-open', (evt) => {
        const cont = evt.target;
        const block = cont?.closest?.('.product-block');
        if (!block || !block.closest('box-builder')) return;
        this.activeBlock = block;
        this.activeBuilder = block.closest('box-builder');
        this.prepareInlineQuickbuy(cont);
      });

      document.addEventListener('on:quickbuy:after-close', (evt) => {
        const block = evt.target?.closest?.('.product-block');
        if (block && block === this.activeBlock) {
          this.activeBlock = null;
        }
      });
    },

    isDesktopQuickbuy() {
      return window.innerWidth >= 768 && theme?.quickBuy;
    },

    handlePickClick(evt) {
      const btn = evt.target.closest('[data-box-builder-pick]');
      if (!btn || btn.disabled) return;

      evt.preventDefault();
      evt.stopPropagation();

      this.activeBuilder = btn.closest('box-builder');
      if (!this.activeBuilder) return;

      const card = btn.closest('.box-builder-card');
      const block = card?.querySelector('product-block') || btn.closest('product-block');

      if (this.isDesktopQuickbuy() && block) {
        this.openInlineQuickbuy(btn, block);
        return;
      }

      this.openDrawer(btn);
    },

    openInlineQuickbuy(btn, block) {
      this.activeBlock = block;

      // Close other expanded quickbuys in this grid (wrapper breaks theme sibling logic)
      const grid = block.closest('.product-grid');
      grid?.querySelectorAll('.product-block.expanded').forEach((el) => {
        if (el === block) return;
        const otherToggle = el.querySelector('a.quickbuy-toggle');
        if (otherToggle) {
          otherToggle.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        }
      });

      const toggle = block.querySelector('a.quickbuy-toggle');
      if (!toggle) {
        this.openDrawer(btn);
        return;
      }

      if (!block.classList.contains('expanded')) {
        toggle.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      } else {
        const cont = block.querySelector('.quickbuy-container');
        if (cont) this.prepareInlineQuickbuy(cont);
      }
    },

    prepareInlineQuickbuy(cont) {
      const detail = cont.querySelector('.inner') || cont;
      const form = detail.querySelector('.js-product-form, form[action*="/cart/add"]');
      const atc = detail.querySelector('[name="add"], .add-to-cart, .quantity-submit-row__submit button');
      if (!form || !atc) {
        // Content may still be loading/spinning — retry shortly
        if (!cont.dataset.boxBuilderRetry) {
          cont.dataset.boxBuilderRetry = '1';
          window.setTimeout(() => {
            delete cont.dataset.boxBuilderRetry;
            if (cont.closest('.product-block.expanded')) this.prepareInlineQuickbuy(cont);
          }, 120);
        }
        return;
      }

      const labelEl = atc.querySelector('.pyjama-atc-button__label');
      if (labelEl) {
        labelEl.textContent = this.addLabel;
      } else {
        atc.textContent = this.addLabel;
      }
      atc.setAttribute('data-add-to-cart-text', this.addLabel);
      atc.classList.add('box-builder-inline-add');

      // Hide dynamic checkout / buy-it-now in box builder quickbuy
      detail.querySelectorAll('.shopify-payment-button, .dynamic-checkout, [data-shopify="payment-button"]').forEach((el) => {
        el.hidden = true;
      });

      if (form.dataset.boxBuilderWired === 'true') {
        this.syncInlineAddState();
        return;
      }
      form.dataset.boxBuilderWired = 'true';

      form.addEventListener(
        'submit',
        (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          evt.stopImmediatePropagation();
          this.handleAddToBox('inline', form);
        },
        true
      );

      atc.addEventListener(
        'click',
        (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          evt.stopImmediatePropagation();
          this.handleAddToBox('inline', form);
        },
        true
      );

      this.syncInlineAddState();
    },

    openDrawer(btn) {
      const productUrl = btn.dataset.productUrl;
      if (!productUrl || !this.drawer) return;

      this.currentProductUrl = productUrl;
      this.setDrawerHeader(btn.dataset.productTitle || '', btn.dataset.productPrice || '');
      this.setDrawerLoading();
      this.drawer.open(btn);

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      btn.classList.add('loading');
      btn.setAttribute('aria-busy', 'true');

      fetch(productUrl, { method: 'GET', signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = html;
          const quickbuyContent = tmpl.content.querySelector('.quickbuy-content');
          if (!quickbuyContent) throw new Error('Quickbuy content not found');

          const detail = quickbuyContent.cloneNode(true);

          if (theme.quickBuy?.convertToQuickBuyContent) {
            theme.quickBuy.convertToQuickBuyContent(detail, productUrl);
          }

          detail.querySelectorAll(
            'modal-dialog, .pyjama-size-chart, .pyjama-mix-match-upsell, .pyjama-mix-match-banner, .box-builder-card__add'
          ).forEach((el) => el.remove());

          tmpl.content.querySelectorAll(':is(.section-main-product, .section-size-guide) link[rel="stylesheet"]').forEach((el) => {
            if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
              document.head.insertAdjacentHTML('beforeend', el.outerHTML);
            }
          });

          tmpl.content.querySelectorAll(':is(.section-main-product, .section-size-guide) script[src]').forEach((el) => {
            if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
              const scr = document.createElement('script');
              scr.src = el.getAttribute('src');
              scr.defer = true;
              document.head.appendChild(scr);
            }
          });

          const body = this.drawer.querySelector('[data-box-builder-drawer-body]');
          const wrapper = document.createElement('div');
          wrapper.className = 'js-quickbuy box-builder-drawer__quickbuy';
          wrapper.dataset.productUrl = productUrl;
          body.innerHTML = '';
          wrapper.appendChild(detail);
          body.appendChild(wrapper);

          wrapper.classList.add('js-product');
          wrapper.querySelectorAll('carousel-slider[data-dynamic-height]').forEach((el) => {
            el.removeAttribute('data-dynamic-height');
          });

          if (typeof window.initPyjamaVariantGalleries === 'function') {
            window.initPyjamaVariantGalleries(wrapper);
          }

          const loadedTitle = detail.querySelector('.product__title, .product-title, h1')?.textContent?.trim();
          const loadedPrice = detail.querySelector('.price__current span:last-child, .price-container .price')?.textContent?.trim();
          this.setDrawerHeader(loadedTitle || btn.dataset.productTitle || '', loadedPrice || btn.dataset.productPrice || '');

          window.setTimeout(() => {
            this.syncDrawerAddState();
            wrapper.querySelectorAll('carousel-slider').forEach((slider) => {
              if (typeof slider.reset === 'function') slider.reset();
              else if (typeof slider.update === 'function') slider.update();
            });
          }, 50);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          const body = this.drawer.querySelector('[data-box-builder-drawer-body]');
          body.innerHTML = '<p class="box-builder-drawer__error">Produkt konnte nicht geladen werden.</p>';
        })
        .finally(() => {
          btn.classList.remove('loading');
          btn.removeAttribute('aria-busy');
        });
    },

    setDrawerHeader(title, price) {
      const titleEl = this.drawer.querySelector('[data-box-builder-drawer-title]');
      const priceEl = this.drawer.querySelector('[data-box-builder-drawer-price]');
      if (titleEl) titleEl.textContent = title;
      if (priceEl) priceEl.textContent = price;
    },

    setDrawerLoading() {
      const body = this.drawer.querySelector('[data-box-builder-drawer-body]');
      body.innerHTML = '<div class="box-builder-drawer__loading"><div class="loading-spinner"></div></div>';
      if (this.addBtn) this.addBtn.disabled = true;
    },

    getSelectedVariant(root) {
      if (!root) return null;
      const variantPicker = root.querySelector('variant-picker');
      const idInput = root.querySelector('.js-product-form input[name="id"], form input[name="id"]');

      let variant = null;
      const jsonEl = variantPicker?.querySelector('script[type="application/json"]');
      if (jsonEl?.textContent) {
        try {
          variant = JSON.parse(jsonEl.textContent);
        } catch {
          variant = null;
        }
      }

      const variantId = idInput?.value ? Number(idInput.value) : (variant?.id ? Number(variant.id) : null);
      if (!variantId) return null;

      if (!variant || Number(variant.id) !== variantId) {
        variant = variantPicker?.variant && Number(variantPicker.variant.id) === variantId
          ? variantPicker.variant
          : { id: variantId, available: true, price: 0 };
      }

      const image = variant.featured_image?.src
        || variant.featured_media?.preview_image?.src
        || root.querySelector('.theme-img')?.currentSrc
        || root.querySelector('.theme-img')?.src
        || '';

      const title =
        this.drawer?.querySelector('[data-box-builder-drawer-title]')?.textContent?.trim()
        || root.querySelector('.product__title, .product-title, h1')?.textContent?.trim()
        || '';
      const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
      const price = Number(variant.price) || 0;

      return {
        variantId,
        productId: variant.product_id,
        title,
        variantTitle,
        image,
        price,
        available: variant.available !== false
      };
    },

    syncDrawerAddState() {
      if (!this.addBtn) return;
      const body = this.drawer?.querySelector('[data-box-builder-drawer-body]');
      const variant = this.getSelectedVariant(body);
      const maxReached = this.activeBuilder && !this.activeBuilder.canAddMore();
      this.addBtn.disabled = !variant || !variant.available || maxReached;
    },

    syncInlineAddState() {
      if (!this.activeBlock) return;
      const atc = this.activeBlock.querySelector('.box-builder-inline-add, [name="add"]');
      if (!atc) return;
      const variant = this.getSelectedVariant(this.activeBlock);
      const maxReached = this.activeBuilder && !this.activeBuilder.canAddMore();
      atc.disabled = !variant || !variant.available || maxReached;
    },

    closeInlineQuickbuy() {
      const block = this.activeBlock;
      if (!block?.classList.contains('expanded')) return;
      const toggle = block.querySelector('a.quickbuy-toggle');
      if (toggle) {
        toggle.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      }
    },

    async handleAddToBox(source, formRoot) {
      const builder = this.activeBuilder;
      const root = source === 'inline'
        ? (formRoot || this.activeBlock)
        : this.drawer?.querySelector('[data-box-builder-drawer-body]');
      const variant = this.getSelectedVariant(root);
      if (!builder || !variant) return;

      if (!variant.available) {
        builder.setError('Diese Variante ist nicht verfügbar.');
        return;
      }

      const label = variant.variantTitle
        ? `${variant.title} — ${variant.variantTitle}`
        : variant.title;

      const added = await builder.addItem({
        variantId: variant.variantId,
        productId: variant.productId,
        title: label,
        variantTitle: variant.variantTitle,
        image: builder.foldedImageFor(variant.variantId, variant.image),
        price: variant.price
      });

      if (!added) return;

      if (source === 'drawer') {
        this.drawer?.close();
      } else {
        this.closeInlineQuickbuy();
      }
    }
  };

  class BoxBuilderDrawer extends SideDrawer {
    connectedCallback() {
      this.addEventListener(`on:${this.dataset.name}:before-open`, () => {
        document.body.classList.add('box-builder-drawer-open');
      });
      this.addEventListener(`on:${this.dataset.name}:before-close`, () => {
        document.body.classList.remove('box-builder-drawer-open');
        if (BoxBuilderPicker.abortController) {
          BoxBuilderPicker.abortController.abort();
        }
        window.setTimeout(() => {
          BoxBuilderPicker.setDrawerLoading();
        }, 300);
      });
    }
  }

  window.BoxBuilderApp = BoxBuilderPicker;

  if (!customElements.get('box-builder')) {
    customElements.define('box-builder', BoxBuilder);
  }

  if (!customElements.get('box-builder-drawer')) {
    customElements.define('box-builder-drawer', BoxBuilderDrawer);
  }

  const boot = () => BoxBuilderPicker.init();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
